import React, { useState } from "react";
import { Button, Grid, ThemeProvider } from "@mui/material";
import { SETable } from "../../../../components/SETable";
import { RiFilter3Line, RiFilterOffLine } from "react-icons/ri";
import { SEText } from "../../../../components/SEText";
import { SEButtonExport } from "../../../../components/SEButtonExport";
import useFilterHandler from "../../../../hooks/useFilterHandler";
import { removeEmptyProperties } from "../../../../utils/utils";
import { formatDateToBR } from "../../../../../utils/time";
import useExcelExport from "../../../../hooks/useExcelExport";
import { useQuery } from "react-query";
import { tableColumns } from "./tableColumns";
import { BlacklistSSEService } from "../services";
import useFormMutation from "../../hooks/useFormMutation";
import { BaSeDialogContext } from "@base/react";
import { handleModalAdmin } from "../../../../../utils/handleModalAdmin";
import SEInputSearch from "../../../../components/SEInputSearch";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import theme from "../../../../mui-theme";
import moment from "moment";
import { cnpjMask } from "../../../../../utils/mask";
import { sortByDirection } from "../../../../../utils/utils";

const Tabela = () => {
  const [searchText, setSearchText] = useState<any>(null);

  const {
    paginationFilters,
    bodyFilters,
    handleResetBodyFilters,
    handleChangeBodyFilter,
    handleLinesPerPage,
    handleChangeOrder,
    handlePageChange,
    handleSearchChange,
  } = useFilterHandler();

  const { showModal } = React.useContext(BaSeDialogContext);

  const handleCleanFilters = () => {
    handleResetBodyFilters();
    setSearchText(null);
  };

  const { data, isLoading, refetch } = useQuery({
    queryFn: () => BlacklistSSEService.findAll(paginationFilters, bodyFilters),
    queryKey: ["solucoesSebraeData", paginationFilters, bodyFilters],
  });

  const { deleteMutation } = useFormMutation({
    showModal,
    service: BlacklistSSEService,
  });

  const { data: filterOptions } = useQuery({
    queryFn: () => BlacklistSSEService.getOptions(),
    queryKey: ["getOptionsArquivosAnexos"],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleDelete = async (id: any) => {
    handleModalAdmin({
      title: `Excluir Registro`,
      content: `Você tem certeza que deseja excluir esse registro?`,
      msgOnSuccess: `Registro excluído com sucesso`,
      onFinish: () => refetch(),
      onConfirmClick: () => deleteMutation(id),
      onCancelClick: () => console.log("modal closed..."),
      showModal: showModal,
      showAPIErrorIfAvailable: true,
    });
  };

  // const handleCreate = () => {
  //   const isCreating = history.location.pathname.includes("/create");
  //   if (!isCreating) {
  //     setFormData(null);
  //     handleShowForm(true);
  //     history.push(history.location.pathname + "/create");
  //   }
  // };

  // const handleEdit = (item: BlacklistSSEInterface) => {
  //   const isEditing = history.location.pathname.includes("/edit");
  //   if (!isEditing) {
  //     setFormData(item);
  //     handleShowForm(true);
  //     history.push(`${history.location.pathname}/${item.id}/edit`);
  //   }
  // };

  const fetchDataExcel = async () => {
    const bodyParams = removeEmptyProperties(bodyFilters);
    const pageFilter = { ...paginationFilters, page: 0, linesPerPage: 10000 };
    const response = await BlacklistSSEService.findAll(pageFilter, bodyParams);

    return response?.content?.map((data: any) => ({
      DATA: data?.data ? moment(data?.data).format("DD/MM/YYYY") : "",
      UF: data?.uf,
      CNPJ: cnpjMask(data?.cnpj),
      PROTOCOLO_SAS: data?.protocoloSas,
      NOME_ATENDENTE: data?.nomeAtendente,
    }));
  };

  const { executeDownload, downloading } = useExcelExport({
    handleDataCallback: fetchDataExcel,
    filename: `Exportacao_Blacklist_SSE_${formatDateToBR(new Date())}`,
    colorLoading: "#3498db",
    isCsv: false,
  });

  return (
    <Grid item>
      <Grid container>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Grid item>
              <Grid item flexDirection="row" display="flex" alignItems="center">
                <Grid
                  item
                  flexDirection="row"
                  display="flex"
                  alignItems="center"
                  mr={3}
                  mb={2}
                >
                  <RiFilter3Line />
                  <SEText as="p" size={"medium"} font="Mulish" weight="bold">
                    Filtros
                  </SEText>
                </Grid>
              </Grid>
              <ThemeProvider theme={theme}>
                <Grid container spacing={2} my={2}>
                  <Grid item md={6}>
                    <SEFilterSelect
                      defaultOption={"Todas"}
                      id={"uf"}
                      placeholder={"Selecione uma das opções"}
                      label="UF"
                      value={bodyFilters?.uf}
                      options={sortByDirection(filterOptions?.uf)}
                      // displayField="sigla"
                      onChange={(value) => handleChangeBodyFilter("uf", value)}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <SEFilterSelect
                      defaultOption={"Todas"}
                      id={"nomeAtendente"}
                      placeholder={"Selecione uma das opções"}
                      label="Nome do atendente"
                      value={bodyFilters?.uf}
                      options={sortByDirection(filterOptions?.nomeAtendente)}
                      onChange={(value) =>
                        handleChangeBodyFilter("nomeAtendente", value)
                      }
                    />
                  </Grid>
                </Grid>

                <Grid container justifyContent={"end"} mb={2}>
                  <Grid
                    item
                    flexDirection="row"
                    display="flex"
                    alignItems="center"
                  >
                    <Button
                      size={"small"}
                      onClick={() => handleCleanFilters()}
                      variant="outlined"
                      startIcon={<RiFilterOffLine />}
                    >
                      <SEText as="p" size={"small"} font="Mulish" weight="bold">
                        Limpar filtros
                      </SEText>
                    </Button>
                  </Grid>
                </Grid>
              </ThemeProvider>
            </Grid>
          </Grid>

          <SETable
            pagination={true}
            headerTableColor={"#7B80FF"}
            tableColor={"#FFFFFF"}
            totalElements={data?.totalElements}
            totalPages={data?.totalPages}
            linesPerPageFilters={paginationFilters?.linesPerPage}
            isLoading={isLoading}
            data={data?.content ?? []}
            page={paginationFilters?.page}
            columns={tableColumns({
              handleDelete,
            })}
            orderBy={paginationFilters?.orderBy}
            direction={paginationFilters?.direction}
            onOrderChange={handleChangeOrder}
            onPagePreviousChange={handlePageChange}
            onPageNextChange={handlePageChange}
            onLinesPerPageChange={handleLinesPerPage}
            tableHeader={
              <>
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  mb={2}
                >
                  <Grid item md={4}>
                    <SEInputSearch
                      value={searchText}
                      onClick={() => handleSearchChange(searchText)}
                      onChange={(e) => setSearchText(e?.target?.value)}
                    />
                  </Grid>

                  <Grid
                    item
                    md={8}
                    justifyContent="flex-end"
                    alignItems="center"
                    display={"flex"}
                    flexDirection={"row"}
                  >
                    {/* <Grid item mr={2}>
                    <Button
                      style={{
                        backgroundColor: "#7B80FF",
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "12px",
                      }}
                      variant="contained"
                      startIcon={<RiAddFill />}
                      onClick={() => handleCreate()}
                    >
                      Incluir
                    </Button>
                  </Grid>  */}
                    <Grid item mr={2}>
                      {!!data?.totalElements && (
                        <SEText as="p" font="Mulish">
                          <strong>{data?.totalElements}</strong>{" "}
                          {data?.totalElements === 1
                            ? "resultado encontrado"
                            : "resultados encontrados"}
                        </SEText>
                      )}
                    </Grid>

                    <Grid item>
                      <SEButtonExport
                        isLoading={downloading}
                        onClick={executeDownload}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Tabela;
